import axios, { AxiosRequestConfig } from 'axios';
import { omitBy } from 'lodash-es';
import { ElMessage } from '@bilibili/magic-cube-plus';
import { mockInterceptorManager } from './mockInterceptor';

// 是否启用 mock
const enableMock = import.meta.env.VITE_USE_MOCK === 'true';

// 创建axios实例
const service = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    timeout: 15000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// 过滤空值
const filterEmptyValues = (obj: any) => {
    return omitBy(
        obj,
        (value) => value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0)
    );
};

// 请求拦截器
service.interceptors.request.use(
    async (config) => {
        // 处理不同请求类型的参数
        if (config.method?.toLowerCase() === 'get') {
            // 处理 GET 请求参数
            config.params = filterEmptyValues(config.params);
        } else {
            // 处理 POST/PUT 请求参数（排除 FormData）
            if (config.data && !(config.data instanceof FormData)) {
                config.data = filterEmptyValues(config.data);
            }
        }

        // 启用 mock 时，检查是否需要拦截请求
        if (enableMock) {
            const mockResponse = mockInterceptorManager.handleRequest(config);
            if (mockResponse) {
                (config as any).mockData = mockResponse;
            }
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 响应拦截器
service.interceptors.response.use(
    (response) => {
        const returnOriginal = (response.config as any)?.returnOriginal;
        const showErrorToast = (response.config as any)?.showErrorToast === false ? false : true;
        const res = response.data;

        if (returnOriginal) {
            return res;
        }

        if (res.code !== 0) {
            showErrorToast && ElMessage.error(res.message || '请求失败');
            return Promise.reject(res);
        }
        return res;
    },
    (error) => {
        // 处理 mock 数据
        if (error.config?.mockData) {
            return error.config.mockData;
        }
        ElMessage.error(error.message || '网络异常');
        return Promise.reject(error);
    }
);

interface RequestOptions extends AxiosRequestConfig {
    loading?: boolean; // 是否显示加载中
    errorMsg?: string; // 自定义错误信息
    successMsg?: string; // 自定义成功信息
    returnOriginal?: boolean; // 是否返回原始数据
    showErrorToast?: boolean; // 是否显示错误信息
}

// GET 请求
export function get<T = any>(url: string, params?: any, options: RequestOptions = {}) {
    return service.request<any, T>({
        method: 'get',
        url,
        params,
        ...options
    });
}

// POST 请求
export function post<T = any>(url: string, params?: any, options: RequestOptions = {}) {
    return service.request<any, T>({
        method: 'post',
        url,
        data: params,
        ...options
    });
}

// PUT 请求
export function put<T = any>(url: string, params?: any, options: RequestOptions = {}) {
    return service.request<any, T>({
        method: 'put',
        url,
        data: params,
        ...options
    });
}

// DELETE 请求
export function del<T = any>(url: string, params?: any, options: RequestOptions = {}) {
    return service.request<any, T>({
        method: 'delete',
        url,
        params,
        ...options
    });
}

// 导出实例和请求方法
export default {
    service,
    get,
    post,
    put,
    delete: del
};
